import type { components } from '@hrworks/api-schema';

import type { Post } from '../types/post';

type Params = Record<string, string | string[] | undefined>;

type FlatPost = { post: Post; postOffer: components['schemas']['PostOffer'] };

/** Fields returned by the API but not yet in the OpenAPI schema */
type PostOfferVisibility = {
  shownInJobApplicationServer?: boolean;
  ignoreDateOfPublication?: boolean;
};

export const isPostOfferVisible = (postOffer: components['schemas']['PostOffer'], today?: string): boolean => {
  const date = today ?? new Date().toISOString().split('T')[0];
  const offer = postOffer as components['schemas']['PostOffer'] & PostOfferVisibility;
  if (!offer.shownInJobApplicationServer) return false;
  if (!offer.ignoreDateOfPublication && offer.publicationDate && offer.publicationDate > date) return false;
  if (offer.validUntilDate && offer.validUntilDate < date) return false;
  return true;
};

const getStringParam = (value: string | string[] | undefined): string =>
  typeof value === 'string' ? value.trim() : '';

// biome-ignore lint/suspicious/noControlCharactersInRegex: intentional sanitization of user input
const CONTROL_CHARS = /[\x00-\x1F\x7F]/g;

/** Normalize text for search: strip control characters, NFC normalization, lowercase */
const normalizeForSearch = (text: string | undefined | null): string =>
  text?.replace(CONTROL_CHARS, '').normalize('NFC').toLocaleLowerCase() ?? '';

export const filterPosts = (posts?: Post[], params?: Params): FlatPost[] => {
  if (!posts) return [];

  const searchQuery = normalizeForSearch(getStringParam(params?.searchQuery));
  const careerLevelKey = getStringParam(params?.level);
  const workingTimeModelKey = getStringParam(params?.time);
  const employmentContractKey = getStringParam(params?.type);
  const scopeOfActivitiesKey = getStringParam(params?.discipline);
  const location = getStringParam(params?.location);

  const checkEstablishment = (establishments: components['schemas']['PermanentEstablishment'][], location: string) =>
    establishments.some(
      (establishment) =>
        `${establishment.address?.city}_${establishment.address?.internationalCountryCode}` === location,
    );

  const matchesSearch = ({ post, postOffer }: FlatPost) =>
    !searchQuery ||
    normalizeForSearch(postOffer.displayName).includes(searchQuery) ||
    normalizeForSearch(post.postDescriptions?.tasks?.title).includes(searchQuery) ||
    normalizeForSearch(post.postDescriptions?.tasks?.description).includes(searchQuery);

  const matchesLocation = ({ postOffer }: FlatPost) =>
    !location ||
    postOffer.placeOfWorkTypeKey === location ||
    (postOffer.permanentEstablishments && checkEstablishment(postOffer.permanentEstablishments, location));

  return posts
    .flatMap((post) => post.postOffers?.map((postOffer) => ({ post, postOffer })) || [])
    .filter((flatPost) => {
      if (!isPostOfferVisible(flatPost.postOffer)) return false;
      if (!matchesSearch(flatPost)) return false;
      if (careerLevelKey && flatPost.postOffer.careerLevelKey !== careerLevelKey) return false;
      if (workingTimeModelKey && flatPost.postOffer.workingTimeModelKey !== workingTimeModelKey) return false;
      if (employmentContractKey && flatPost.postOffer.employmentContractKey !== employmentContractKey) return false;
      if (scopeOfActivitiesKey && flatPost.post.scopeOfActivitiesKey !== scopeOfActivitiesKey) return false;
      if (!matchesLocation(flatPost)) return false;

      return true;
    });
};
